import { useRef, useCallback, useEffect } from 'react';
import { createLogger } from '../utils/logger';

/**
 * 门店刷新时间管理 Hook
 * 为每个门店记录最后刷新时间，并在门店进入可见范围时检查是否需要自动刷新
 * 同时支持定期检查所有可见门店是否需要刷新
 */
export interface UseStoreRefreshTimeParams {
  onRefresh?: (storeId: number | string) => void; // 刷新回调函数
  refreshInterval?: number; // 刷新间隔（毫秒），默认5分钟
  visibleStoreIds?: Set<number | string>; // 可见的门店ID集合，用于定期检查
  enablePeriodicCheck?: boolean; // 是否启用定期检查，默认true
  checkInterval?: number; // 定期检查间隔（毫秒），默认10秒
}

export interface UseStoreRefreshTimeResult {
  recordRefreshTime: (storeId: number | string) => void; // 记录刷新时间
  checkAndRefresh: (storeId: number | string) => void; // 检查并刷新（如果需要）
  getRefreshTime: (storeId: number | string) => number | null; // 获取刷新时间
  getVisibleStoreIds: () => Set<number | string>; // 获取可见门店ID集合（用于更新）
}

const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000; // 默认5分钟
const DEFAULT_CHECK_INTERVAL = 60 * 1000; // 默认60秒检查一次，避免每10秒触发多店刷新导致 CPU/请求爆表

export function useStoreRefreshTime(
  params?: UseStoreRefreshTimeParams
): UseStoreRefreshTimeResult {
  const logger = createLogger('useStoreRefreshTime');
  
  const {
    onRefresh,
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    visibleStoreIds,
    enablePeriodicCheck = true,
    checkInterval = DEFAULT_CHECK_INTERVAL,
  } = params || {};

  // 使用 ref 存储每个门店的刷新时间，避免触发重新渲染
  const refreshTimesRef = useRef<Map<number | string, number>>(new Map());

  // 使用 ref 存储刷新回调，确保总是使用最新的回调
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // 使用 ref 存储可见门店ID集合，确保总是使用最新的值
  const visibleStoreIdsRef = useRef<Set<number | string>>(visibleStoreIds || new Set());

  // 当 visibleStoreIds prop 变化时，更新 ref
  useEffect(() => {
    if (visibleStoreIds) {
      visibleStoreIdsRef.current = visibleStoreIds;
    }
  }, [visibleStoreIds]);

  /**
   * 记录门店的刷新时间
   */
  const recordRefreshTime = useCallback((storeId: number | string) => {
    const now = Date.now();
    refreshTimesRef.current.set(storeId, now);
    logger.debug(`记录门店 ${storeId} 刷新时间: ${new Date(now).toLocaleTimeString()}`);
  }, [logger]);

  /**
   * 检查门店是否需要刷新，如果需要则触发刷新
   */
  const checkAndRefresh = useCallback((storeId: number | string) => {
    const lastRefreshTime = refreshTimesRef.current.get(storeId);

    if (!lastRefreshTime) {
      // 如果没有记录，说明是第一次加载，不需要刷新
      logger.debug(`门店 ${storeId} 没有刷新时间记录，跳过检查`);
      return;
    }

    const now = Date.now();
    const timeSinceRefresh = now - lastRefreshTime;

    if (timeSinceRefresh >= refreshInterval) {
      logger.info(`门店 ${storeId} 距离上次刷新已超过 ${Math.round(refreshInterval / 1000 / 60)} 分钟，触发自动刷新`, {
        lastRefreshTime: new Date(lastRefreshTime).toLocaleTimeString(),
        timeSinceRefresh: Math.round(timeSinceRefresh / 1000 / 60) + ' 分钟',
        refreshInterval: refreshInterval / 1000 / 60 + ' 分钟',
      });

      // 触发刷新
      if (onRefreshRef.current) {
        onRefreshRef.current(storeId);
        // 刷新后立即更新刷新时间
        recordRefreshTime(storeId);
      }
    } else {
      logger.debug(`门店 ${storeId} 距离上次刷新仅 ${Math.round(timeSinceRefresh / 1000)} 秒，无需刷新`, {
        lastRefreshTime: new Date(lastRefreshTime).toLocaleTimeString(),
        timeSinceRefresh: Math.round(timeSinceRefresh / 1000) + ' 秒',
        remainingTime: Math.round((refreshInterval - timeSinceRefresh) / 1000) + ' 秒',
      });
    }
  }, [logger, refreshInterval, recordRefreshTime]);

  /**
   * 获取门店的刷新时间
   */
  const getRefreshTime = useCallback((storeId: number | string): number | null => {
    return refreshTimesRef.current.get(storeId) || null;
  }, []);

  /**
   * 获取可见门店ID集合（用于更新）
   */
  const getVisibleStoreIds = useCallback(() => {
    return visibleStoreIdsRef.current;
  }, []);

  /**
   * 定期检查所有可见门店是否需要刷新
   * 即使页面不动，也会定期检查并触发刷新（如果需要）
   */
  useEffect(() => {
    if (!enablePeriodicCheck) {
      return;
    }

    const intervalId = setInterval(() => {
      const visibleIds = visibleStoreIdsRef.current;
      if (visibleIds.size === 0) {
        return; // 没有可见门店，跳过检查
      }

      // 每轮只刷新「最陈旧」的一家，避免多店同时刷新导致 CPU/请求爆表
      const now = Date.now();
      let oldestStoreId: number | string | null = null;
      let oldestTime = 0;
      visibleIds.forEach((storeId) => {
        const lastRefreshTime = refreshTimesRef.current.get(storeId);
        if (!lastRefreshTime) return;
        const elapsed = now - lastRefreshTime;
        if (elapsed >= refreshInterval && elapsed > oldestTime) {
          oldestTime = elapsed;
          oldestStoreId = storeId;
        }
      });
      if (oldestStoreId != null) {
        logger.debug(`定期检查：本轮只刷新最陈旧门店 ${oldestStoreId}`);
        checkAndRefresh(oldestStoreId);
      }
    }, checkInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [enablePeriodicCheck, checkInterval, checkAndRefresh, logger, refreshInterval]);

  return {
    recordRefreshTime,
    checkAndRefresh,
    getRefreshTime,
    getVisibleStoreIds,
  };
}
